<template>
  <div class="rivenditore-page">
    <template v-if="storeData">
      <div class="rivenditore-page__dealer-banner-container">
        <div class="rivenditore-page__back">
          <client-only>
            <nuxt-link
              :to="backTo"
              class="rivenditore-page__back-link"
              :aria-label="$t('actions.back')">
              <base-icon name="back" />
            </nuxt-link>
          </client-only>
        </div>

        <breadcrumb
          class="rivenditore-page__dealer-breadcrumb"
          :items="breadcrumbItems"
          :negative="true" />

        <!-- :pre-title="bannerPreTitle" -->
        <dealer-banner
          class="rivenditore-page__dealer-banner"
          :title="bannerTitle"
          :sub-title="bannerSubTitle"
          :show-cta="false"
          :background-image="storeData?.coverImage?.src"
          :background-image-webp="storeData?.coverImage?.webp"
          :background-image-avif="storeData?.coverImage?.avif" />
      </div>

      <intro-text
        :title="$t('dealer.page.title')"
        :primary-tag="$t('header.dealers')"
        :secondary-tag="storeData.province"
        :show-tag="true"
        :show-left-tag="true"
        :show-right-tag="true"
        :show-body="false"
        :show-cta="false"
        :tag-clickable="false"
        :use-custom-template="true"
        @tag-click="handleTagClick">
        <dealer-brand
          :big-logo="true"
          :logo-url="storeSingleBrand ? null : getFirst(storeData.storeLogo)?.src"
          :secondary-logo-url="storeSingleBrand ? getFirst(storeData.storeLogo)?.src : null"
          :location="capitalize(storeData.logoSubtitle, true)"
          :second-location="storeData.logoSecondSubtitle ? capitalize(storeData.logoSecondSubtitle, true) : null" />
      </intro-text>

      <!-- CTA Navigation Row -->
      <dealer-navigation
        v-if="navigationItems.length > 0"
        :items="navigationItems"
        @item-click="handleNavigationClick" />

      <!-- Bold Text Section -->
      <div
        v-if="storeData.city"
        class="text-section">
        <custom-text
          :text="$t('dealer.page.locationText', { location: storeData.city })"
          color="black" />
      </div>

      <!-- Mini Cards Section -->
      <div class="mini-cards-section">
        <div class="mini-cards-container">
          <!-- Card 1 - Calendar -->

          <veneta-icon-wrapper
            v-slot="{ venetaIconProps }"
            :icon-width="24"
            :icon-height="24"
            icon-color="red">
            <mini-card
              :text="$t('dealer.miniCards.help')"
              :clickable="true"
              @click="scrollToForm">
              <template #icon>
                <rivenditore-icon />
              </template>
              <template #arrow>
                <veneta-icon v-bind="venetaIconProps" />
              </template>
            </mini-card>
          </veneta-icon-wrapper>

          <!-- Card 2 - Rivenditore -->
          <veneta-icon-wrapper
            v-slot="{ venetaIconProps }"
            :icon-width="24"
            :icon-height="24"
            icon-color="red">
            <mini-card
              :text="$t('dealer.miniCards.appointment')"
              :clickable="true"
              @click="scrollToForm">
              <template #icon>
                <calendar-icon />
              </template>
              <template #arrow>
                <veneta-icon v-bind="venetaIconProps" />
              </template>
            </mini-card>
          </veneta-icon-wrapper>

          <!-- Card 3 - Rivenditore -->
          <veneta-icon-wrapper
            v-slot="{ venetaIconProps }"
            :icon-width="24"
            :icon-height="24"
            icon-color="red">
            <mini-card
              :text="$t('dealer.miniCards.info')"
              :clickable="true"
              @click="scrollToForm">
              <template #icon>
                <calendar-icon />
              </template>
              <template #arrow>
                <veneta-icon v-bind="venetaIconProps" />
              </template>
            </mini-card>
          </veneta-icon-wrapper>
        </div>

        <div class="review-rating-container">
          <review-rating
            v-if="!!storeData.reviewRating"
            :rating="storeData.reviewRating"
            :rating-size="24" />
        </div>
      </div>
      <!-- Information Row Section -->
      <dealer-info-row
        v-if="dealerData"
        :dealer-info="dealerData"
        @directions-click="handleDirectionsClick"
        @kitchen-display-click="handleKitchenDisplayClick" />

      <template v-if="promotions.length > 0">
        <!-- Iniziative Section -->
        <div class="iniziative-section">
          <intro-text
            :title="$t('dealer.sections.initiatives')"
            :show-tag="false"
            :show-body="false"
            :show-cta="false" />
        </div>

        <!-- Image/Video Banner Section -->
        <div
          v-for="promo in promotions"
          :key="promo.id"
          class="image-banner-section">
          <div
            class="image-banner-container"
            :class="{ 'image-banner-container--clickable': promo._type === 'promoBoost' || promo.initiativeDescription || promo.externalLink?.url }"
            @click="handleBannerCtaClick(promo)">
            <!-- PromoBoost with video -->
            <base-media
              v-if="promo._type === 'promoBoost' && getPromoMedia(promo)"
              :intersection="false"
              :media="getPromoMedia(promo)"
              :autoplay="true"
              :muted="true"
              class="banner-media" />

            <!-- PromoBoost image fallback (desktop + mobile) -->
            <template v-else-if="promo._type === 'promoBoost'">
              <img
                v-if="getFirst(promo.promoHeaderMedia?.[0]?.headerMediaMobile)?.src"
                :src="getFirst(promo.promoHeaderMedia[0].headerMediaMobile).src"
                :alt="promo.title || $t('dealer.images.initiativesBanner')"
                class="banner-image banner-image--mobile" />
              <img
                v-if="getFirst(promo.promoHeaderMedia?.[0]?.headerMedia)?.src"
                :src="getFirst(promo.promoHeaderMedia[0].headerMedia).src"
                :alt="promo.title || $t('dealer.images.initiativesBanner')"
                class="banner-image banner-image--desktop" />
            </template>

            <!-- Initiative image -->
            <img
              v-else
              :src="getFirst(promo.initiativeImage)?.src || getFirst(promo.initiativeThumbnail)?.src"
              :alt="promo.title || $t('dealer.images.initiativesBanner')"
              class="banner-image" />
          </div>

          <!-- CTA below image -->
          <div
            v-if="promo.initiativeDescription || promo._type === 'promoBoost' || promo.externalLink?.url"
            class="banner-cta-section">
            <cta
              :text="$t('dealer.cta.discoverMore')"
              variant="text"
              color="red"
              :show-icon="false"
              @click="handleBannerCtaClick(promo)" />
          </div>
        </div>
      </template>

      <!-- Two Column Info Section -->
      <two-column-info-section
        :title="$t('dealer.sections.whoWeAre')"
        :description="whoWeAreDescription"
        :cta-text="$t('dealer.cta.readMore')"
        @cta-click="handleWhoWeAreCtaClick" />

      <!-- Showroom Section -->
      <div id="showroom">
        <two-column-info-section
          :title="$t('dealer.sections.showroom')"
          :description="showroomDescription"
          :cta-text="$t('dealer.cta.readMore')"
          @cta-click="handleShowroomCtaClick" />
      </div>

      <!-- Second Image Banner Section -->
      <!-- <div class="image-banner-section image-banner-section--no-cta">
      <div class="image-banner-container">
        <img
          :src="horizontalImage"
          :alt="$t('dealer.images.showroomBanner')"
          class="banner-image" />
      </div>
    </div> -->

      <template v-if="exists(storeData.gallery)">
        <block-image
          v-if="storeData.gallery.length == 1"
          contained
          :image="getFirst(storeData.gallery)" />
        <div
          v-else
          class="dealer-gallery">
          <block-gallery
            :overlay="true"
            :show-title="false"
            :items="storeData.gallery"
            :config="{ slidesPerView: 1, spaceBetween: 0 }" />
        </div>
      </template>

      <!-- Dealer Services Section -->
      <div
        v-if="servicesData.length > 0"
        id="services">
        <dealer-services
          :title="$t('dealer.services.title')"
          image-src="/images/pngs/service.png"
          :image-alt="$t('dealer.images.servicesImage')"
          :items-count="servicesData.length"
          list-max-height="90vh">
          <!-- Dynamic service items -->
          <service-list-item
            v-for="service in servicesData"
            :key="service.id"
            :text="service.text"
            :show-top-border="true">
            <template #icon>
              <img
                v-if="service.icon?.src"
                :src="service.icon.src"
                :alt="service.icon.alt || service.text"
                class="service-icon" />
              <div
                v-else
                class="service-icon service-icon--fallback">
                <!-- Fallback icon -->
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle
                    cx="16"
                    cy="16"
                    r="8"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none" />
                  <circle
                    cx="16"
                    cy="16"
                    r="4"
                    fill="currentColor" />
                </svg>
              </div>
            </template>
          </service-list-item>
        </dealer-services>
      </div>

      <!-- Dealer Team Section -->
      <div
        v-if="teamMembers.length > 0 || teamDescription"
        id="team">
        <!-- <client-only> -->
        <dealer-team
          :pre-title="$t('dealer.team.boldText')"
          :title="$t('dealer.team.title')"
          :description="teamDescription"
          :show-ctas="false"
          :team-members="teamMembers" />
      <!-- </client-only> -->
      </div>

      <!-- Dealer Contacts Form Section -->
      <dealer-contacts-form
        id="contact-form"
        :phone-number="storeData.storePhone"
        :whatsapp-number="storeData.storeWhatsappBusinessPhone || storeData.storeWhatsappMobile"
        :email="storeData.publicContactEmail || 'info@venetacucine.com'"
        :store="storeData.company || storeData.storeLegalName || storeData.storeName"
        :show-call-button="Boolean(storeData.storePhone)"
        :show-whatsapp-button="Boolean(storeData.storeWhatsappBusinessPhone || storeData.storeWhatsappMobile)"
        :show-location-button="Boolean(storeLocation.lat && storeLocation.lng)"
        @call-click="handlePhoneClick"
        @whatsapp-click="handleWhatsappClick"
        @location-click="handleDirectionsClick"
        @form-submit="handleFormSubmit" />

      <!-- Cucine in esposizione Section -->
      <div
        v-if="kitchenCards.length > 0"
        id="kitchens-on-display"
        class="iniziative-section cucine-esposizione-section">
        <intro-text
          :title="$t('dealer.sections.kitchensOnDisplay')"
          :show-tag="false"
          :show-body="false"
          :show-cta="false" />
      </div>

      <!-- Kitchen Cards Section -->
      <div
        v-if="kitchenCards.length > 0"
        class="kitchen-cards-section">
        <div class="kitchen-cards-container">
          <card-contents
            v-for="kitchen in kitchenCards"
            :key="kitchen.id"
            :title="kitchen.title"
            :image-src="kitchen.imageSrc"
            :images="kitchen.images"
            :cta-text="kitchen.ctaText"
            :show-badge="kitchen.showBadge"
            :badge-text="kitchen.badgeText"
            :badge-color="kitchen.badgeColor"
            @cta-click="handleKitchenCardClick(kitchen)" />
        </div>
      </div>

      <!-- Occasioni Section -->
      <div
        v-if="occasioniCards.length > 0"
        id="occasions"
        class="occasioni-section">
        <div class="occasioni-intro">
          <intro-text
            :title="$t('dealer.sections.occasions')"
            :show-tag="false"
            :show-body="false"
            :show-cta="false"
            title-color="red" />
        </div>

        <div class="occasioni-cards-container">
          <card-contents
            v-for="occasione in occasioniCards"
            :key="occasione.id"
            :title="occasione.title"
            :always-show-title="true"
            :image-src="occasione.imageSrc"
            :images="occasione.images"
            :show-badge="occasione.showBadge"
            :badge-text="occasione.badgeText"
            :badge-color="occasione.badgeColor"
            :compact="true"
            :show-cta="true"
            :cta-text="$t('dealer.cta.discoverMore')"
            @cta-click="handleDealCardClick(occasione)">
            <!-- Custom pricing content -->
            <div class="pricing-content">
              <div class="pricing-row pricing-row--listino">
                {{ $t("dealer.pricing.listPrice") }}
                <span class="price-strikethrough">{{ occasione.prezzoListino }}</span>
              </div>
              <div class="pricing-row pricing-row--outlet">
                {{ $t("dealer.pricing.outletPrice") }}
                <span class="price-bold">{{ occasione.prezzoOutlet }}</span>
              </div>
            </div>
          </card-contents>
        </div>
      </div>

      <!-- Two Column Story Section -->
      <div
        v-if="reviewCards.length > 0"
        id="life-stories">
        <two-column-story
          :logo-src="RaccontiImage"
          :logo-alt="$t('dealer.stories.lifeStories') + ' Veneta Cucine'"
          :bold-text="$t('dealer.stories.lifeStories')"
          title=""
          :description="$t('dealer.stories.lifeStoriesDescription')"
          :show-title="false"
          image-src="/images/jpgs/1.jpg"
          :image-alt="$t('dealer.images.modernKitchen')"
          :inverted="false"
          :show-ctas="true"
          :primary-cta-text="$t('dealer.cta.discoverStories')"
          :secondary-cta-text="$t('dealer.cta.sendStory')"
          cta-variant="text"
          :show-cta-icon="false"
          :primary-cta-to="localePath({ name: 'life-stories' })"
          :secondary-cta-to="localePath({ name: 'life-stories', query: { form: 1 } })" />

        <!-- Two Column Story Section - Inverted -->
        <two-column-story
          :description="$t('dealer.stories.innovationDescription')"
          :show-title="false"
          :show-logo="false"
          image-src="/images/jpgs/2.jpg"
          :image-alt="$t('dealer.images.technologicalKitchen')"
          :inverted="true"
          :show-ctas="false" />
      </div>

      <!-- I racconti dei nostri clienti Section -->
      <div
        v-if="reviewCards.length > 0"
        class="racconti-clienti-section">
        <div class="racconti-clienti-intro">
          <intro-text
            :title="$t('dealer.sections.clientStories')"
            :show-tag="false"
            :show-body="false"
            :show-cta="false"
            title-color="black" />
        </div>

        <!-- Kitchen Review Cards List -->
        <kitchen-review-list
          :reviews="reviewCards"
          :max-visible="6"
          :cta-text="$t('dealer.cta.seeAll')"
          :see-all-to="localePath({ name: 'life-stories' })"
          @load-more="handleLoadMoreReviews" />
      </div>

      <!-- contacts -->

      <dealer-contacts
        v-if="storeData.storePhone || storeData.storeWhatsappBusinessPhone || storeData.storeWhatsappMobile || dealerPlace"
        :title="$t('dealer.contacts.title')"
        :contact-info="$t('dealer.contacts.contactInfo')"
        :phone="storeData.storePhone"
        :phone-href="phoneHref"
        :maps-url="storeData.googleMapsUrl"
        :whatsapp="storeData.storeWhatsappBusinessPhone || storeData.storeWhatsappMobile"
        :whatsapp-href="whatsappHref"
        :place="dealerPlace"
        @phone-click="handlePhoneClick"
        @whatsapp-click="handleWhatsappClick">
      </dealer-contacts>

      <!-- NEWS Section -->
      <news-carousel
        id="news"
        :news="newsData.length > 0 ? newsData : null"
        :label="$t('dealer.cta.allNews')"
        :title="$t('dealer.sections.news')" />

      <!-- Information Section -->
      <!-- <div class="information-section">
      <horizontal-card
        :title="$t('dealer.info.needMoreInfo')"
        :description="null"
        :image-src="horizontalImage"
        :primary-cta-text="$t('dealer.cta.contacts')"
        :secondary-cta-text="$t('dealer.cta.findDealer')"
        :show-ctas="true"
        @primary-cta-click="handleContactsClick"
        @secondary-cta-click="handleFindDealerClick" />
    </div> -->

      <!-- Dealer Footer Section -->
      <dealer-footer
        :book-appointment-cta="true"
        :whatsapp-cta="!!(storeData.storeWhatsappBusinessPhone || storeData.storeWhatsappMobile)
        "
        :phone-cta="!!storeData.storePhone"
        :location-cta="!!(storeLocation.lat && storeLocation.lng)"
        :phone-href="phoneHref"
        :whatsapp-href="whatsappHref"
        :cta-text="$t('dealer.cta.bookAppointment')"
        @cta-click="scrollToForm"
        @fab-click-phone="handlePhoneClick"
        @fab-click-whatsapp="handleWhatsappClick"
        @fab-click-location="handleDirectionsClick" />

      <!-- Kitchen Modal -->
      <teleport to="body">
        <base-modal
          v-if="selectedKitchen"
          :show="showKitchenModal"
          fullscreen
          no-padding
          :show-close-button="false"
          @close="closeKitchenModal">
          <template #default>
            <kitchen-modal
              :kitchen="selectedKitchen"
              @close="closeKitchenModal" />
          </template>
        </base-modal>
      </teleport>

      <!-- Deal Modal -->
      <teleport to="body">
        <base-modal
          v-if="selectedDeal"
          :show="showDealModal"
          fullscreen
          no-padding
          :show-close-button="false"
          @close="closeDealModal">
          <template #default>
            <deal-modal
              :deal="selectedDeal"
              @close="closeDealModal" />
          </template>
        </base-modal>
      </teleport>

      <teleport to="body">
        <base-modal
          :show="modal"
          @close="modal = false">
          <template #default>
            <form-builder
              :id="`block-form-reservedArea`"
              class="rivenditore-page__form"
              handle="bookAppointment"
              :params="dealerData ? [
                { key: 'dealerEmail', value: dealerData?.publicContactEmail || 'info@venetacucine.com' },
                { key: 'dealerName', value: dealerData?.company || dealerData?.storeLegalName || dealerData?.storeName },
              ] : null"
              @submitted="onsubmit" />
          </template>
        </base-modal>
      </teleport>
    </template>
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import OCCASIONE_PLACEHOLDER from '~/assets/images/occasioni-placeholder.png'
  import RaccontiImage from '~/assets/images/racconti.png'
  import CalendarIcon from '~/assets/svgs/icons/icon-calendar.svg'
  import RivenditoreIcon from '~/assets/svgs/icons/icon-rivenditore.svg'
  import Cta from '~/components/Cta.vue'
  import CustomText from '~/components/CustomText.vue'
  import DealerContacts from '~/components/dealer/DealerContacts.vue'
  import DealerContactsForm from '~/components/dealer/DealerContactsForm.vue'
  import DealerServices from '~/components/dealer/DealerServices.vue'
  import DealerTeam from '~/components/dealer/DealerTeam.vue'
  import DealModal from '~/components/dealer/DealModal.vue'
  import KitchenModal from '~/components/dealer/KitchenModal.vue'
  import KitchenReviewList from '~/components/dealer/KitchenReviewList.vue'
  import ServiceListItem from '~/components/dealer/ServiceListItem.vue'
  import NewsCarousel from '~/components/global/Block/NewsCarousel.vue'
  import ReviewRating from '~/components/ReviewRating.vue'
  import TwoColumnInfoSection from '~/components/TwoColumnInfoSection.vue'
  import TwoColumnStory from '~/components/TwoColumnStory.vue'
  import { useBackNavigation } from '~/composables/useBackNavigation'
  import { useLocalBusinessSchema } from '~/composables/useStructuredData'
  import { LIFE_STORIES_BY_STORE } from '~/graphql/pages/life-story'
  import { GLOBAL_STORE_INITIATIVES, NEWS } from '~/graphql/pages/listing'
  import { getStoreBySlug, parseStoreHours } from '~/graphql/stores/deta-fetch'
  import { capitalize } from '~/utils/functions'
  import { getMedia } from '~/utils/media'
  import { buildTelUrl, buildWaMeUrl } from '~/utils/phone'
  import { getStoreLocatorTarget } from '~/utils/storeLocatorTarget'
  import { buildDirectionsUrl } from '~/utils/stores'
  import { pushDataLayer } from '~/utils/tracking'

  const {
    t, locale, localeCodes
  } = useI18n()
  const localePath = useLocalePath()
  const env = useRuntimeConfig().public

  const { bus } = useNuxtApp()

  // --------------------------------- DATA FETCHING ------------------------------

  // Get slug from route params
  const { params } = useRoute()

  // Smart back navigation - returns to previous page or store locator
  const { backTo } = useBackNavigation({ name: 'store-locator' })
  const { previewToken, previewParam } = usePreview()

  const { data, error: storeError } = await useAsyncData(`store-data-${locale.value}-${params?.slug}`, async () => {
    const result = await getStoreBySlug(params?.slug, {
      previewToken: previewToken.value,
      previewParam: previewParam.value
    })
    const store = getFirst(result)
    if (!store) {
      // fatal: senza, in navigazione client-side l'errore non renderizza la
      // pagina 404 e la route resta bloccata in caricamento infinito
      throw createError({
        statusCode: 404, statusMessage: 'Store not found', fatal: true
      })
    }
    return result
  })

  if (storeError.value || !data.value) {
    throw storeError.value ?? createError({
      statusCode: 404, statusMessage: 'Store not found', fatal: true
    })
  }

  // Scheda dettaglio disattivata su questa lingua (es. store italiani fuori
  // dal sito it): si riporta l'utente allo store locator del suo territorio,
  // con la card evidenziata, invece che su una 404 o sulla landing generica.
  //
  // Solo in SSR: in navigazione client-side navigateTo qui interrompe la
  // navigazione in corso e la pagina resta bloccata in transizione (vedi
  // middleware/store-details.js, che copre quel caso).
  if (import.meta.server && !getFirst(data.value)?.detailsPageEnabled) {
    const target = getStoreLocatorTarget(getFirst(data.value))

    await navigateTo(
      target ? localePath(target) : localePath('store-locator'),
      { redirectCode: 302 }
    )
  }

  useSeoData(data)

  const getRandomCoverImage = () => {
    const randr = useState('dealer-cover-random', () => Math.floor(Math.random() * 5))

    const randomNumber = randr.value + 1
    return {
      src: `/images/jpgs/cover-${randomNumber}.jpg`,
      webp: `/images/webp/cover-${randomNumber}.webp 1920w, /images/webp/cover-${randomNumber}-768.webp 768w`,
      avif: `/images/avif/cover-${randomNumber}.avif 1920w, /images/avif/cover-${randomNumber}-768.avif 768w`
    }
  }

  const _store = getFirst(data.value)

  const storeData = ref(_store ? {
    ..._store,
    province: getFirst(_store.province)?.title,
    // Il fragment store non espone una cover da CMS: si usa sempre una cover
    // statica random (con varianti webp/avif). Se in futuro si aggiunge il
    // campo `coverImage` al fragment, ripristinare qui il ramo CMS.
    coverImage: getRandomCoverImage(),
    title: `${t('dealer.page.showroomTitle', { location: getFirst(_store.province)?.title })}`
  } : null)

  const storeLocation = computed(() => {
    const [ lat, lng ] = storeData.value?.location?.coordinates.split(',') ?? []

    return {
      lat,
      lng
    }
  })

  const phoneHref = computed(() => buildTelUrl(storeData.value?.storePhone))
  const whatsappHref = computed(() => buildWaMeUrl(
    storeData.value?.storeWhatsappBusinessPhone || storeData.value?.storeWhatsappMobile
  ))

  const setI18nParams = useSetI18nParams()

  setI18nParams(localeCodes.value.reduce((acc, _locale) => {
    if (_locale == locale.value) {
      acc[_locale] = {
        slug: params.slug
      }
    } else {
      const localized = data.value?.localized?.find(lp => lp.site == _locale)
      acc[_locale] = {
        // slug null = lingua esclusa da switcher e hreflang: la scheda
        // dettaglio esiste solo sui siti dove il flag è attivo
        slug: localized?.detailsPageEnabled ? localized.slug : null
      }
    }
    return acc
  }, {}))

  // Fetch life stories related to this store
  const { data: storeLifeStories } = await useAsyncData(
    `store-life-stories-${params?.slug}`,
    () => useApi(LIFE_STORIES_BY_STORE, {
      storeId: storeData.value?.id
    }), {
      transform: r => r.stories || []
    }
  )

  // Fetch first global initiative for all stores
  const { data: globalStoreInitiative } = await useAsyncData(
    `global-store-initiative-${locale.value}`,
    () => useApi(GLOBAL_STORE_INITIATIVES), {
      transform: r => (r.initiatives && r.initiatives.length ? r.initiatives[0] : null)
    }
  )

  // Fetch global news
  const { data: globalNewsData } = await useAsyncData(
    `global-news-${locale.value}`,
    () => useApi(NEWS), {
      transform: r => r.news || []
    }
  )

  const dealerData = computed(() => {
    if (!storeData.value) return null

    const store = storeData.value

    // Build full address
    const fullAddress = [
      store.storeAddress,
      store.zipCode,
      store.city,
      store.province ? `(${store.province})` : ''
    ]
      .filter(Boolean)
      .join(', ')

    return {
      ...store,
      address: {
        full: fullAddress
      },
      contacts: {
        phone: store.storePhone || null,
        whatsapp: store?.storeWhatsappBusinessPhone || store?.storeWhatsappMobile || null,
        email: store.publicContactEmail || store.internalWebContactEmail || null,
        website: store.websiteUrl?.url || null,
        socials: {
          instagram: store.instagramUrl?.url || null,
          facebook: store.facebookUrl?.url || null,
          linkedin: store.linkedinUrl?.url || null,
          x: store.xUrl?.url || null,
          tiktok: store.tikTokUrl?.url || null,
          youtube: store.youTubeUrl?.url || null,
          pinterest: store.pinterestUrl?.url || null
        }
      },
      hours: parseStoreHours(store, locale.value, t)?.openingHours
      // hours: mockOpeningHours
    }
  })

  // Place computed for map
  const dealerPlace = computed(() => {
    if (!storeData.value) return null

    const store = storeData.value

    if (!storeLocation.value.lat || !storeLocation.value.lng) return null

    return {
      lat: storeLocation.value.lat,
      lng: storeLocation.value.lng,
      title: store.title || store.storeLegalName,
      address: store.storeAddress ? `${store.storeAddress}, ${store.city}` : store.city
    }
  })

  const promotions = computed(() => {
    const storePromotions = storeData.value?.promotions || []
    const globalInitiative = globalStoreInitiative.value
    const activePromoBoost = storeData.value?.activePromoBoost?.[0]

    const result = []
    if (globalInitiative) {
      result.push(globalInitiative)
    }
    if (storeData.value?.showHeaderMediaInDealerCard && activePromoBoost) {
      result.push({ ...activePromoBoost, _type: 'promoBoost' })
    } else if (storePromotions.length > 0) {
      result.push(storePromotions[0])
    }
    return result
  })

  const getPromoMedia = promo => {
    const block = promo?.promoHeaderMedia?.[0]
    if (!block || block.typeHandle !== 'video') return null
    return getMedia({ video: [ block ] })
  }

  // ------------------------------------------------------------------------------

  // --------------------------------- MOCKED DATA -------------------------------

  const breadcrumbItems = computed(() => [
    { label: t('navigation.breadcrumb.home'), href: '/' },
    { label: t('navigation.breadcrumb.dealers'), href: localePath('store-locator') },
    { label: storeData.value?.storeLegalName, current: true }
  ])

  const navigationItems = computed(() => {
    const items = []

    if (storeData.value?.aboutShowroom) {
      items.push({ section: 'showroom', text: t('dealer.navigation.showroom') })
    }

    if (servicesData.value.length > 0) {
      items.push({ section: 'services', text: t('dealer.navigation.services') })
    }

    if (teamMembers.value.length > 0) {
      items.push({ section: 'team', text: t('dealer.navigation.team') })
    }

    if (kitchenCards.value.length > 0) {
      items.push({
        section: 'kitchens-on-display',
        text: t('dealer.navigation.kitchensOnDisplay')
      })
    }

    if (occasioniCards.value.length > 0) {
      items.push({ section: 'occasions', text: t('dealer.navigation.occasions') })
    }

    items.push({ section: 'news', text: t('dealer.navigation.news') })

    if (reviewCards.value.length > 0) {
      items.push({ section: 'life-stories', text: t('dealer.navigation.lifeStories') })
    }

    return items
  })

  const servicesData = computed(() => {
    if (!storeData.value?.services) return []

    return [
      ...storeData.value.services.map((service, index) => ({
        id: index + 1,
        text: service.title,
        icon: getFirst(service.serviceIcon)
      }))

    // ...storeData.value.services.map((service, index) => ({
    //   id: index + 1,
    //   text: service.title,
    //   icon: getFirst(service.serviceIcon)
    // })),

    // ...storeData.value.services.map((service, index) => ({
    //   id: index + 1,
    //   text: service.title,
    //   icon: getFirst(service.serviceIcon)
    // }))
    ]
  })

  const teamMembers = computed(() => {
    if (!storeData.value?.team) return []

    return storeData.value.team.map(member => {
      // Verifica se teamPrivacyPdf esiste e non è vuoto
      // teamPrivacyPdf è un campo Assets con maxRelations: 1, quindi può essere:
      // - null/undefined se non presente
      // - un array vuoto se non presente
      // - un array con un elemento se presente
      const hasPrivacyPdf = member.teamPrivacyPdf
        && (Array.isArray(member.teamPrivacyPdf) ? member.teamPrivacyPdf.length > 0 : true)

      return {
        image: hasPrivacyPdf ? (getFirst(member.image)?.src || null) : null,
        imageAlt: hasPrivacyPdf ? `${member.title} photo` : null,
        name: member.name,
        role: member.role
      }
    })
  })

  const kitchenCards = computed(() => {
    if (!storeData.value?.showroomKitchens) return []

    return storeData.value.showroomKitchens.map((kitchen, index) => {
      const mediaArray = kitchen.media
        ? Array.isArray(kitchen.media)
          ? kitchen.media
          : [ kitchen.media ]
        : []

      const processedImages = mediaArray.length > 0
        ? mediaArray.map(img => {
          const asset = getFirst(img) || img
          return {
            src: asset?.src || asset?.srcOriginal,
            webp: asset?.webp,
            avif: asset?.avif,
            srcLarge: asset?.srcLarge || asset?.src || asset?.srcOriginal,
            webpLarge: asset?.webpLarge,
            avifLarge: asset?.avifLarge,
            alt: asset?.alt || `${kitchen.title} - Image`
          }
        })
        : []

      return {
        id: index + 1,
        title: kitchen.title,
        imageSrc: processedImages.length === 1 ? processedImages[0].src : null,
        images: processedImages,
        ctaText: t('dealer.cta.discoverMore'),
        showBadge: !!kitchen.label,
        badgeText: !!kitchen.label ? t(`general.${String(kitchen.label).toLowerCase()}`) : null,
        badgeColor: 'red',
        description: kitchen.description || null,
        finishes: kitchen.finishes || null,
        appliances: kitchen.appliances || null
      }
    })
  })

  const occasioniCards = computed(() => {
    if (!storeData.value?.dealsKitchens) return []

    return storeData.value.dealsKitchens.map((deal, index) => {
      const dealImages = Array.isArray(deal.dealImage) ? deal.dealImage : deal.dealImage ? [ deal.dealImage ] : []
      const mediaImages = Array.isArray(deal.media) ? deal.media : deal.media ? [ deal.media ] : []
      const allImages = dealImages.length > 0 ? dealImages : mediaImages

      const processedImages = allImages.map(img => {
        const asset = getFirst(img) || img
        return {
          src: asset?.src || asset?.srcOriginal,
          webp: asset?.webp,
          avif: asset?.avif,
          srcLarge: asset?.srcLarge || asset?.src || asset?.srcOriginal,
          webpLarge: asset?.webpLarge,
          avifLarge: asset?.avifLarge,
          alt: asset?.alt || `${deal.title} - Image`
        }
      })

      // Fallback: occasioni senza immagine mostrano un placeholder (card + modale)
      // invece del riquadro vuoto. Vedi anche il blocco lato CMS che rende
      // obbligatoria l'immagine in fase di pubblicazione.
      const finalImages = processedImages.length > 0
        ? processedImages
        : [
          {
            src: OCCASIONE_PLACEHOLDER,
            srcLarge: OCCASIONE_PLACEHOLDER,
            alt: deal.title || t('dealer.sections.occasions')
          }
        ]

      return {
        id: index + 1,
        title: deal.title,
        imageSrc: finalImages.length === 1 ? finalImages[0].src : null,
        images: finalImages,
        showBadge: !!deal.label,
        badgeText: !!deal.label ? t(`general.${String(deal.label).toLowerCase()}`) : null,
        badgeColor: 'red',
        prezzoListino: deal.catalogPrice,
        prezzoOutlet: deal.outletPrice,
        description: deal.description || null,
        measurements: deal.measurements || null,
        specifications: deal.specifications || null
      }
    })
  })

  const reviewCards = computed(() => {
    if (!storeLifeStories.value || storeLifeStories.value.length === 0) return []

    return storeLifeStories.value.map((story, index) => ({
      id: story.id || index + 1,
      slug: story.slug,
      firstname: story.firstname,
      message: story.message,
      date: story.date || story.dateCreated,
      kitchen: story.kitchen,
      image: exists(story.images) ? getFirst(story.images) : null
    }))
  })

  const newsData = computed(() => {
    const pickedNews = (storeData.value?.news || []).map(news => ({
      ...news,
      categories: { title: t('dealer.news.dealerNewsCategory'), slug: getFirst(news.categories)?.slug }
    }))
    const globalNews = globalNewsData.value || []

    // Combine picked news and global news
    const combinedNews = [ ...pickedNews, ...globalNews ]

    // Remove duplicates by id
    const uniqueNews = Array.from(new Map(combinedNews.map(item => [ item.id, item ])).values())

    // Filter out news without a date and sort by date descending
    return uniqueNews
      .filter(news => news.date || news.dateCreated)
      .filter(news => news.date !== null && news.dateCreated !== null)
      .sort((a, b) => {
        const dateA = new Date(a.date || a.dateCreated || 0)
        const dateB = new Date(b.date || b.dateCreated || 0)
        return dateB - dateA
      })
  })

  const storeSingleBrand = computed(() => {
    if (!storeData.value) return false
    return getStoreType(storeData.value) === STORE_TYPE.MONOBRAND ? true : false
  })

  const bannerTitle = computed(() => {
    if (!storeData.value) return ''

    if (storeSingleBrand.value) {
      if (!storeData.value.logoSubtitle) {
        return storeData.value.storeLegalName?.toLowerCase().includes('veneta cucine')
          ? `Veneta Cucine</br>${storeData.value.storeLegalName.replace(/veneta\s+cucine/i, '').trim()}`
          : storeData.value.storeLegalName
      }

      return `Veneta Cucine<br/>${capitalize(storeData.value.logoSubtitle, true)}`
    }

    return storeData.value.storeName || ''
  })

  const bannerSubTitle = computed(() => {
    if (!storeData.value?.logoSecondSubtitle) return null
    return capitalize(storeData.value.logoSecondSubtitle, true)
  })

  // const bannerPreTitle = computed(() => {
  //   if (!storeData.value) return null

  //   if (storeSingleBrand.value) {
  //     // Single brand: pre-title is storeLegalName
  //     const preTitle = storeData.value.storeLegalName
  //     // Only show if different from the title text content
  //     // Remove HTML tags for comparison and normalize case
  //     const titleText = bannerTitle.value.replace(/<br\s*\/?>/gi, ' ')
  //     const preTitleNormalized = preTitle?.trim().toLowerCase() ?? ''
  //     const titleTextNormalized = titleText?.trim().toLowerCase() ?? ''
  //     return preTitle && preTitleNormalized !== titleTextNormalized ? preTitle : null
  //   }

  //   return storeData.value.storeName
  // })

  const teamDescription = computed(() => {
    return storeData.value?.teamDescription?.html || ''
  })

  const whoWeAreDescription = computed(() => {
    if (storeData.value.aboutUs) {
      return typeof storeData.value.aboutUs === 'string'
        ? storeData.value.aboutUs
        : storeData.value.aboutUs.html || ''
    }

    const city = storeData.value.city || ''
    const cityUpper = city.toUpperCase()
    const address = storeData.value.storeAddress || ''
    const phone = storeData.value.storePhone || ''
    const email = storeData.value.publicContactEmail || storeData.value.internalWebContactEmail || ''

    let storeName = storeData.value.storeName

    if (storeSingleBrand.value) {
      if (!storeData.value.logoSubtitle) {
        storeName = storeData.value.storeLegalName
      }

      storeName = `Veneta Cucine ${capitalize(storeData.value.logoSubtitle, true)}`
    }

    const phoneSegment = phone ? t('dealer.fallback.phoneSegment', { phone }) : ''
    const emailSegment = email ? t('dealer.fallback.emailSegment', { email }) : ''

    return t('dealer.fallback.whoWeAre', {
      storeName,
      city,
      cityUpper,
      address,
      phoneSegment,
      emailSegment
    })
  })

  const showroomDescription = computed(() => {
    if (storeData.value.aboutShowroom) {
      return typeof storeData.value.aboutShowroom === 'string'
        ? storeData.value.aboutShowroom
        : storeData.value.aboutShowroom.html || ''
    }

    const city = storeData.value.city || ''
    const cityUpper = city.toUpperCase()

    let storeName = storeData.value.storeName

    if (storeSingleBrand.value) {
      if (!storeData.value.logoSubtitle) {
        storeName = storeData.value.storeLegalName
      }

      storeName = `Veneta Cucine ${capitalize(storeData.value.logoSubtitle, true)}`
    }

    return t('dealer.fallback.showroom', {
      storeName,
      city,
      cityUpper
    })
  })

  // ------------------------------------------------------------------------------
  // Modal state
  const modal = ref(false)
  const showKitchenModal = ref(false)
  const selectedKitchen = ref(null)
  const showDealModal = ref(false)
  const selectedDeal = ref(null)

  // ------------------------------------------------------------------------------

  const handleTagClick = async () => {
    await navigateTo('/')
  }

  const handlePhoneClick = () => {
    pushDataLayer({
      name: storeData.value.storeName || storeData.value.storeLegalName || storeData.value?.company,
      shop: `Veneta cucine a ${storeData.value.city}`,
      provincia: storeData.value.province,
      tipologia_rivenditore: storeData.value.isSpecialistSingleBrand ? 'monomarca' : 'plurimarca',
      event: 'click_phone_retailer',
      tel: storeData.value.storePhone
    })
  }

  const handleWhatsappClick = () => {
    const whatsapp = storeData.value.storeWhatsappBusinessPhone || storeData.value.storeWhatsappMobile
    pushDataLayer({
      name: storeData.value.storeName || storeData.value.storeLegalName || storeData.value?.company,
      shop: `Veneta cucine a ${storeData.value.city}`,
      provincia: storeData.value.province,
      tipologia_rivenditore: storeData.value.isSpecialistSingleBrand ? 'monomarca' : 'plurimarca',
      event: 'click_whatsapp_retailer',
      tel: whatsapp
    })
  }

  const handleDirectionsClick = () => {
    const _url = buildDirectionsUrl(storeData.value)

    if (_url) {
      window.open(_url, '_blank')

      pushDataLayer({
        name: storeData.value.storeName || storeData.value.storeLegalName || storeData.value?.company,
        shop: `Veneta cucine a ${storeData.value.city}`,
        provincia: storeData.value.province,
        tipologia_rivenditore: storeData.value.isSpecialistSingleBrand ? 'monomarca' : 'plurimarca',
        event: 'click_maps_retailer'
      })
    }
  }

  const scrollToForm = () => {
    bus.emit('scroll:scroll-to', {
      target: '#contact-form',
      options: {
        duration: 1.2,
        easing: t => 1 - Math.pow(1 - t, 4)
      }
    })
  }

  const handleKitchenDisplayClick = () => {
    handleNavigationClick({ item: { section: 'kitchens-on-display' } })
  }

  const handleNavigationClick = ({ item }) => {
    if (item.section) {
      const target = `#${item.section}`

      bus.emit('scroll:scroll-to', {
        target,
        options: {
          duration: 1.2,
          easing: t => 1 - Math.pow(1 - t, 4)
        }
      })
    }
  }

  const handleBannerCtaClick = promo => {
    if (promo._type === 'promoBoost') {
      navigateTo(localePath({ name: 'promotions-slug', params: { slug: promo.slug } }))
      return
    }
    if (promo?.externalLink?.url) {
      window.open(promo.externalLink.url, '_blank', 'noopener')
      return
    }
    if (promo?.slug && promo?.initiativeDescription) {
      navigateTo(entryPath(promo))
    }
  }

  const handleWhoWeAreCtaClick = () => {
    // console.log('Who We Are CTA clicked')
  }

  const handleShowroomCtaClick = () => {
    // console.log('Showroom CTA clicked')
  }

  const handleKitchenCardClick = kitchen => {
    selectedKitchen.value = kitchen
    showKitchenModal.value = true
  }

  const handleDealCardClick = deal => {
    selectedDeal.value = deal
    showDealModal.value = true
  }

  const closeKitchenModal = () => {
    showKitchenModal.value = false
    selectedKitchen.value = null
  }

  const closeDealModal = () => {
    showDealModal.value = false
    selectedDeal.value = null
  }

  const handleLoadMoreReviews = () => {
    // console.log('Load more reviews clicked')
  }

  const handleFormSubmit = formData => {
    pushDataLayer({
      event: 'formSubmission-form-rivenditore',
      name: storeData.value.storeName || storeData.value.storeLegalName || storeData.value?.company,
      tipologia_rivenditore: storeData.value?.isSpecialistSingleBrand ? 'monomarca' : 'plurimarca',
      shop: `Veneta cucine a ${storeData.value?.city}`,
      provincia: storeData.value?.province,
      cutomer_firstname: `${formData?.firstname}`,
      cutomer_lastname: `${formData?.lastname}`,
      cutomer_address: `${formData?.address}`,
      cutomer_city: `${formData?.city}`,
      cutomer_email: formData?.email,
      cutomer_phone: formData?.phone
    })
  }

  const onsubmit = params => {
    modal.value = false

    pushDataLayer({
      event: 'formSubmission-form-rivenditore',
      name: storeData.value.storeName || storeData.value.storeLegalName || storeData.value?.company,
      tipologia_rivenditore: storeData.value?.isSpecialistSingleBrand ? 'monomarca' : 'plurimarca',
      shop: `Veneta cucine a ${storeData.value?.city}`,
      provincia: storeData.value?.province,
      cutomer_firstname: `${params?.firstname}`,
      cutomer_lastname: `${params?.lastname}`,
      cutomer_email: params?.email,
      cutomer_address: params?.address,
      cutomer_phone: params?.phone,
      cutomer_city: params?.city
    })
  }

  onMounted(() => {
    const route = useRoute()
    bus.on('page:after-enter', () => {
      if (route.query.appuntamento === '1' || route.query.appointment === '1') {
        // modal.value = true
        scrollToForm()
      }
    })
  })

  // ----------------------------- STRUCTURED DATA -----------------------------
  // Add LocalBusiness schema
  const localBusinessSchema = computed(() =>
    useLocalBusinessSchema(parseStoreHours(storeData.value, locale.value, t), env.BASE_URL, locale.value)
  )

  watchEffect(() => {
    if (localBusinessSchema.value) {
      useJsonld(localBusinessSchema.value)
    }
  })

  definePageMeta({
    // Redirect allo store locator per le schede non pubblicate, in navigazione
    // client-side (in SSR se ne occupa il blocco sopra).
    middleware: 'store-details',
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss" scoped>
.rivenditore-page {
  min-height: 100vh;
  position: relative;
  padding-bottom: var(--space-tera);

  &__form :deep(.form-loader__field .cta[type="submit"]) {
    background-color: var(--color-red);
  }

  &__dealer-banner-container {
    position: relative;
  }

  &__back {
    position: absolute;
    top: calc(var(--header-height) + 10px);
    left: var(--grid-margin);
    z-index: 3;
  }

  &__back-link {
    width: rem(60px);
    height: rem(60px);
    display: flex;
    align-items: center;
    color: var(--color-white);
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.5;
    }

    :deep(svg),
    :deep(svg path) {
      fill: var(--color-white);
      stroke: var(--color-white);
    }
  }

  &__dealer-breadcrumb {
    position: absolute;
    top: calc(var(--header-height) + 20px);
    z-index: 2;
  }

  &__dealer-banner {
    z-index: 1;
  }

  @include up-to-tablet {
    padding-bottom: var(--space-kilo);
  }

  :deep(.intro-text) {
    margin-top: var(--space-tera);
    margin-bottom: calc(var(--space-m) * 2);
    padding: 0 var(--grid-margin);
    max-width: 100%;

    .intro-text__tag--primary {
      text-align: left;
    }

    .intro-text__tag--secondary {
      text-align: right;
    }

    @include up-to-tablet {
      margin-top: var(--space-kilo);
      margin-bottom: var(--space-m);
      padding: 0 var(--space-s);
    }
  }
}

.text-section {
  padding: 0 var(--grid-margin);
  margin-bottom: var(--space-xl);
  text-align: center;

  @include up-to-tablet {
    display: none;
  }
}

.mini-cards-section {
  padding: 0 var(--grid-margin);
  margin-bottom: var(--space-xxl);

  @include up-to-tablet {
    padding: 0 var(--space-s);
    margin-bottom: var(--space-s-desktop);

    .veneta-icon-wrapper {
      width: 100%;
    }
  }
}

.review-rating-container {
  display: flex;
  justify-content: center;
  margin-top: var(--space-m);
}

.mini-cards-container {
  display: flex;
  justify-content: center;
  gap: var(--space-n);
  flex-wrap: wrap;

  @include up-to-tablet {
    gap: var(--space-s);
    flex-direction: column;
    align-items: center;
  }
}

.information-section {
  padding: var(--space-kilo) var(--grid-margin);

  @include up-to-tablet {
    padding: var(--space-kilo) var(--space-s);
  }
}

.iniziative-section {
  margin-top: var(--space-kilo-mobile);
  padding: 0 var(--grid-margin);
  text-align: center;

  @include up-to-tablet {
    margin-top: var(--space-xl);
    padding: 0 var(--space-s);
  }

  :deep(.intro-text) {
    padding: 0;
    margin: 0;
  }
}

.image-banner-section {
  margin-top: var(--space-xl);
  margin-bottom: calc(var(--space-xl) + var(--space-kilo));

  @include up-to-tablet {
    margin-top: var(--space-l);
    margin-bottom: calc(var(--space-l) + var(--space-kilo));
  }
}

.image-banner-container {
  padding: 0 156px;

  &--clickable {
    cursor: pointer;
  }

  @include up-to-tablet {
    padding: 0 var(--space-s);
  }
}

.banner-image {
  width: 100%;
  height: auto;
  max-height: 70vh;
  object-fit: contain;
  display: block;

  &--mobile {
    @include mq(medium) {
      display: none;
    }
  }

  &--desktop {
    display: none;

    @include mq(medium) {
      display: block;
    }
  }
}

.banner-media {
  max-height: 70vh;

  :deep(video),
  :deep(img) {
    max-height: 70vh;
    object-fit: contain;
  }
}

.banner-cta-section {
  text-align: center;
  margin-top: var(--space-m);
}

:deep(.dealer-services) {
  margin-top: var(--space-mega);
  margin-bottom: var(--space-mega);

  @include up-to-tablet {
    padding: 0 var(--space-s);
    margin-top: var(--space-kilo);
    margin-bottom: var(--space-kilo);
  }
}

.service-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-red);
  object-fit: contain;

  &--fallback {
    svg {
      width: 100%;
      height: 100%;
    }
  }
}

.news-title {
  padding: 0 var(--space-m-desktop);

  @include up-to-tablet {
    margin-bottom: var(--space-l);
  }
}

.image-banner-section--no-cta {
  margin-bottom: var(--space-xxl);

  @include up-to-tablet {
    margin-bottom: var(--space-xl);
  }
}

.kitchen-cards-section {
  margin-top: var(--space-xl);
  display: flex;
  justify-content: center;
  padding-left: 40px;
  padding-right: 40px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
}

.kitchen-cards-container {
  max-width: 100vw;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-m) 30px;

  @include up-to-tablet {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xl) 0;
  }
}

.occasioni-section {
  background-color: var(--color-light-grey);
  padding: var(--space-kilo) 0;
  margin-top: var(--space-xl);

  @include up-to-tablet {
    margin-top: var(--space-l);
  }
}

.occasioni-intro {
  text-align: center;
  margin-bottom: var(--space-xl);

  @include up-to-tablet {
    margin-bottom: var(--space-l);
  }

  :deep(.intro-text) {
    padding: 0;
    margin: 0;

    .intro-text__title {
      color: var(--color-red);
    }
  }
}

.occasioni-cards-container {
  padding: var(--space-m);
  display: grid;
  grid-template-columns: repeat(2, 430px);
  gap: var(--space-xxl) 30px;
  justify-content: center;

  @include up-to-tablet {
    grid-template-columns: minmax(0, 430px);
    gap: var(--space-xl) 0;
    padding: var(--space-m) var(--space-s);
  }
}

.pricing-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: var(--space-xxs);
}

.pricing-row {
  font-size: var(--text-b-n, 16px);
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;

  &--listino {
    color: var(--color-black);
  }

  &--outlet {
    color: var(--color-red);
  }
}

.price-strikethrough {
  text-decoration: line-through;
}

.price-bold {
  font-weight: 700;
}

.racconti-clienti-section {
  background-color: var(--color-light-grey);
  padding: var(--space-kilo) 0;
  margin-top: var(--space-xl);

  @include up-to-tablet {
    margin-top: var(--space-l);
  }
}

.racconti-clienti-intro {
  text-align: center;
  margin-bottom: 80px;

  @include up-to-tablet {
    margin-bottom: 60px;
  }

  :deep(.intro-text) {
    padding: 0;
    margin: 0;

    .intro-text__title {
      color: var(--color-black);
    }
  }
}

:deep(.card-contents__title) {
  font-size: var(--typography-size-m, 20px);
  font-weight: 500;
  max-width: 430px;
  text-align: center;
}

// Ensure fullscreen modals fill properly
:deep(.base-modal.-fullscreen) {
  .base-modal__dialog {
    overflow: hidden !important;
    display: flex;
    flex-direction: column;
    height: 100vh !important;
    max-height: 100vh !important;
    margin: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
  }

  .base-modal__content {
    margin: 0 !important;
    height: 100% !important;
    flex: 1;
    overflow: hidden !important;
    display: flex;
    flex-direction: column;
  }

  .kitchen-modal,
  .deal-modal {
    flex: 1;
    display: flex;
    overflow: hidden;
    height: 100%;
  }
}

// Prevent horizontal scroll in modal
:deep(.base-modal) {
  overflow-x: hidden;

  .base-modal__dialog {
    overflow-x: hidden;
  }
}

.dealer-gallery {
  :deep(.swiper) {
    overflow: hidden;
  }

  :deep(.swiper-slide) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto !important;
  }

  :deep(.swiper-slide .image) {
    width: 100%;
    height: auto !important;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.swiper-slide .image > img) {
    width: auto !important;
    height: auto !important;
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain !important;
    object-position: center !important;
    display: block;
    margin: 0 auto;
  }

  :deep(.gallery__overlay) {
    position: relative;
    display: flex;
    justify-content: center;
    padding-top: 12px;
  }

  :deep(.gallery__caption) {
    position: static;
    border-radius: 0;
    background-color: transparent;
    padding: 6px 0;
  }

  :deep(.gallery__pagination) {
    position: static;
    border-radius: 0;
    background-color: transparent;
    padding: 6px 0;
  }
}
</style>
